import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

import noticeBanner from "../assets/election-notice.png";
import voterListPDF from "../assets/provisional-voter-list.pdf";
import finalListBanner from "../assets/final-voter-list.png";
import finalVoterListPDF from "../assets/final-voter-list.pdf";

/* ✅ NEW: List of Nominations assets (image + pdf) */
import nominationBanner from "../assets/list-of-nominations.png";
import nominationListPDF from "../assets/list-of-nominations.pdf";

import ValidnominationBanner from "../assets/Validlist-of-nominations.png";
import ValidnominationListPDF from "../assets/Validlist-of-nominations.pdf";

import FinalValidnominationBanner from "../assets/FinalValidlist-of-nominations.png";
import FinalValidnominationListPDF from "../assets/FinalValidlist-of-nominations.pdf";

/* ---------------- Premium Toolbar Button ---------------- */
const Tb = ({ children, onClick, title }) => (
  <button
    title={title}
    onClick={onClick}
    className="px-3 py-1.5 rounded-md text-xs font-semibold bg-white/90 hover:bg-white shadow border border-gray-200"
  >
    {children}
  </button>
);

/* ---------------- Zoomable Preview Modal ---------------- */
function ZoomPreviewModal({ open, onClose, src, title, pdfUrl }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [isDragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0, tx: 0, ty: 0 });

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const img = el.querySelector("img");
    if (!img) return;
    const vw = el.clientWidth - 24;
    const vh = el.clientHeight - 24 - 48;
    const rw = img.naturalWidth || img.width || 1200;
    const rh = img.naturalHeight || img.height || 1600;
    const s = Math.min(vw / rw, vh / rh);
    setScale(clamp(s, 0.2, 4));
    setTx(0);
    setTy(0);
  }, []);

  useEffect(() => {
    if (open) {
      const t = setTimeout(fit, 50);
      return () => clearTimeout(t);
    }
  }, [open, fit, src]);

  const zoomBy = (delta, pivot = null) => {
    const newScale = clamp(scale + delta, 0.25, 5);
    if (pivot && newScale !== scale) {
      const rect = containerRef.current.getBoundingClientRect();
      const cx = pivot.clientX - rect.left - rect.width / 2;
      const cy = pivot.clientY - rect.top - rect.height / 2;
      const k = newScale / scale;
      setTx(tx * k + cx * (1 - k));
      setTy(ty * k + cy * (1 - k));
    }
    setScale(newScale);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    zoomBy(delta, e);
  };

  const onPointerDown = (e) => {
    setDragging(true);
    setStart({ x: e.clientX, y: e.clientY, tx, ty });
  };
  const onPointerMove = (e) => {
    if (!isDragging) return;
    setTx(start.tx + (e.clientX - start.x));
    setTy(start.ty + (e.clientY - start.y));
  };
  const onPointerUp = () => setDragging(false);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+") zoomBy(0.1);
      if (e.key === "-") zoomBy(-0.1);
      if (e.key.toLowerCase() === "f") fit();
      if (e.key === "0") setScale(1);
    },
    [onClose, fit]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, handleKey]);

  const onDblClick = (e) => {
    if (scale > 1.01) {
      fit();
    } else {
      zoomBy(0.75, e);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm"
      >
        <div className="absolute inset-0 flex flex-col">
          {/* Toolbar */}
          <div className="px-4 py-3 flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <span className="text-white/90 text-sm font-semibold">{title}</span>
              <span className="text-white/60 text-xs">| {Math.round(scale * 100)}%</span>
            </div>
            <div className="flex gap-2">
              <Tb title="Zoom out ( - )" onClick={() => zoomBy(-0.1)}>－</Tb>
              <Tb title="Zoom in ( + )" onClick={() => zoomBy(0.1)}>＋</Tb>
              <Tb title="Fit (F)" onClick={fit}>Fit</Tb>
              <Tb title="Actual Size (0)" onClick={() => setScale(1)}>100%</Tb>
              <a
                href={src}
                download
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                ⬇ Download
              </a>
              {pdfUrl && (
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  📄 Open PDF
                </a>
              )}
              <Tb title="Close (Esc)" onClick={onClose}>✕</Tb>
            </div>
          </div>

          {/* Canvas */}
          <div
            ref={containerRef}
            onWheel={onWheel}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onDoubleClick={onDblClick}
            className={`flex-1 m-3 rounded-2xl bg-white/95 shadow-[0_0_0_1px_rgba(255,255,255,0.2)_inset] 
              border border-white/20 overflow-hidden select-none cursor-${isDragging ? "grabbing" : "grab"}`}
          >
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={src}
                alt={title}
                draggable={false}
                style={{
                  transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                  transformOrigin: "center center",
                  transition: isDragging ? "none" : "transform 120ms ease-out",
                }}
                className="max-w-none will-change-transform"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ---------------- Image Card with Hover Zoom & Modal ---------------- */
function ImageCard({ title, caption, imgSrc, pdfUrl, accent = "green" }) {
  const [open, setOpen] = useState(false);
  const border = accent === "fuchsia" ? "border-fuchsia-300" : "border-emerald-300";

  return (
    <>
      <div className={`group bg-white rounded-2xl border ${border} shadow-xl p-4`}>
        <div className="relative overflow-hidden rounded-xl border">
          <img
            src={imgSrc}
            alt={title}
            className="w-full h-56 object-contain transition-transform duration-500 ease-out group-hover:scale-110 cursor-zoom-in"
            onClick={() => setOpen(true)}
          />
          <div className="absolute right-3 top-3 flex gap-2 opacity-0 group-hover:opacity-100 transition">
            <a
              href={imgSrc}
              download
              className="px-2.5 py-1 rounded-full text-xs font-semibold text-white bg-black/60 hover:bg-black"
            >
              ⬇ Image
            </a>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="px-2.5 py-1 rounded-full text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700"
              >
                📄 PDF
              </a>
            )}
          </div>
        </div>
        <div className="mt-3">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-[13px] text-gray-600 mt-0.5">{caption}</p>
        </div>
      </div>

      <ZoomPreviewModal
        open={open}
        onClose={() => setOpen(false)}
        src={imgSrc}
        title={title}
        pdfUrl={pdfUrl}
      />
    </>
  );
}

/* ---------------- Premium Schedule ---------------- */
function Schedule({ items }) {
  return (
    <div className="rounded-2xl bg-white shadow-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-emerald-700 to-lime-600 text-white">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <span>📅</span> Election Schedule
        </h3>
      </div>
      <div className="grid md:grid-cols-2 gap-4 p-5">
        {items.map((i, idx) => (
          <div
            key={idx}
            className={`p-3.5 rounded-xl border transition 
              ${i.highlight
                ? "bg-fuchsia-50 border-fuchsia-200 shadow-[0_8px_30px_-12px_rgba(217,70,239,0.45)]"
                : "bg-white border-gray-200 hover:shadow-md"}`}
          >
            <div className="text-xs text-gray-500">
              {i.date}{i.time && ` • ${i.time}`}
            </div>
            <div className={`mt-0.5 text-[15px] font-semibold ${i.highlight ? "text-fuchsia-700" : "text-gray-800"}`}>
              {i.text}
            </div>
          </div>
        ))}
      </div>
      <div className="px-5 pb-4 text-xs text-gray-500">
        * 8 Directors: 5 General, 2 Women, 1 SC/ST
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */
export default function ElectionNotice() {
  const timeline = [
    { date: "01 Aug 2025", time: "11:00 AM", text: "Provisional Voter List Published" },
    { date: "04–07 Aug 2025", time: "11:00 AM – 3:00 PM", text: "Objections on Provisional List" },
    { date: "08–12 Aug 2025", time: "11:00 AM – 3:00 PM", text: "Scrutiny of Objections" },
    { date: "13 Aug 2025", time: "11:00 AM", text: "Final Voter List Published"},
    { date: "18–21 Aug 2025", time: "11:00 AM – 3:00 PM", text: "Nomination Form Issuing / Filing" },
    { date: "21 Aug 2025", time: "5:00 PM", text: "List of Nomination Forms"  },
    { date: "22 Aug 2025", time: "11:00 AM onwards", text: "Scrutiny of Received Forms" },
    { date: "22 Aug 2025", time: "5:00 PM", text: "Valid Nomination List Published"},
    { date: "23 Aug 2025", time: "11:00 AM – 3:00 PM", text: "Withdrawal of Nominations" },
    { date: "23 Aug 2025", time: "5:00 PM", text: "Final List of Candidates",   },
    { date: "31 Aug 2025", time: "8:00 AM – 4:00 PM", text: "Polling Day (cutoff 2:00 PM)" },
    { date: "01 Sept 2025", time: "8:00 AM onwards", text: "Counting of Votes" },
    { date: "04 Sept 2025", text: "Declaration of Result (by CEA, New Delhi,)", highlight: true },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50 to-emerald-100 py-8 px-4 md:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-800 via-lime-600 to-emerald-800">
            🗳️ Election Notification
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Official notices & voter lists as per Multi-State Cooperative Societies Act, 2002 (amended 2023)
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <ImageCard
            title="Provisional Voter List"
            caption={
              <>
                published on <span className="text-emerald-700 font-semibold">01 Aug 2025</span>. Objections accepted until{" "}
                <span className="text-red-700 font-semibold">10 Aug 2025</span>.
              </>
            }
            imgSrc={noticeBanner}
            pdfUrl={voterListPDF}
            accent="green"
          />
          <ImageCard
            title="Final Voter List (13 Aug • 11:00 AM)"
            caption={<span>published on <span className="text-fuchsia-700 font-semibold">13 Aug 2025</span>.</span>}
            imgSrc={finalListBanner}
            pdfUrl={finalVoterListPDF}
            accent="fuchsia"
          />

          {/* ✅ NEW: PUBLICATION OF LIST OF NOMINATIONS RECEIVED (3rd card) */}
          <ImageCard
            title="PUBLICATION OF LIST OF NOMINATIONS RECEIVED (21 Aug • 5:00 PM)"
            caption={<span>published on <span className="text-emerald-700 font-semibold">21 Aug 2025</span>.</span>}
            imgSrc={nominationBanner}
            pdfUrl={nominationListPDF}
            accent="green"
          />

          {/* ✅ NEW: PUBLICATION OF LIST OF VALID NOMINATIONS  (4rd card) */}
          <ImageCard
            title="PUBLICATION OF LIST OF VALID NOMINATIONS  (22 Aug • 5:00 PM)"
            caption={<span>published on <span className="text-emerald-700 font-semibold">22 Aug 2025</span>.</span>}
            imgSrc={ValidnominationBanner}
            pdfUrl={ValidnominationListPDF}
            accent="green"
          />

          {/* ✅ NEW: Final List of Candidates (5:00 PM)  (4rd card) */}
          <ImageCard
            title="Final List of Candidates  (23 Aug • 5:00 PM)"
            caption={<span>published on <span className="text-emerald-700 font-semibold">23 Aug 2025</span>.</span>}
            imgSrc={FinalValidnominationBanner}
            pdfUrl={FinalValidnominationListPDF}
            accent="green"
          />
        </div>

        {/* Schedule */}
        <Schedule items={timeline} />
      </div>
    </div>
  );
}
